
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DocumentTable } from '@/components/documents/DocumentTable';
import { DocumentFilters } from '@/components/documents/DocumentFilters';
import { DocumentViewer } from '@/components/documents/DocumentViewer';
import { useDocuments } from '@/hooks/useDocuments';
import { enhancedDocumentStorage } from '@/services/storage/enhancedDocumentStorage';
import { ArrowLeft, FileText, Search, Filter, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const DocumentManagementPage = () => {
  const { documents, loading, deleteDocument, downloadDocument, refreshDocuments } = useDocuments();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [storageStats, setStorageStats] = useState({
    totalDocuments: 0,
    totalSize: 0,
    totalSizeFormatted: '0 B',
    chunkedDocuments: 0,
    storageUsage: '0%'
  });

  // Load storage stats and documents
  useEffect(() => {
    loadStorageStats();
    console.log('📋 DocumentManagementPage mounted - loading 500+ capacity documents');
    
    // Force refresh documents on mount to ensure they are loaded
    refreshDocuments();
  }, [refreshDocuments]);

  // Refresh when documents change and ensure proper loading
  useEffect(() => {
    loadStorageStats();
    console.log(`📊 Documents updated: ${documents.length} total documents loaded`);
    
    // Log all documents for debugging
    console.log('📋 All documents in state:', documents);
    
    // Log student-assigned documents
    const studentDocs = documents.filter(doc => doc.studentId && doc.studentName);
    console.log(`👥 Student-assigned documents: ${studentDocs.length}`);
    
    studentDocs.forEach(doc => {
      console.log(`📄 "${doc.name}" → ${doc.studentName} (ID: ${doc.studentId})`);
    });

    // Also log unassigned documents
    const unassignedDocs = documents.filter(doc => !doc.studentId);
    console.log(`📝 Unassigned documents: ${unassignedDocs.length}`);
  }, [documents]);

  const loadStorageStats = () => {
    const stats = enhancedDocumentStorage.getStorageStats();
    setStorageStats(stats);
    console.log('📊 Storage stats loaded:', stats);
  };

  // Enhanced filtering to include student name and student ID search
  const filteredDocuments = documents.filter(doc => {
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = doc.name.toLowerCase().includes(searchLower) ||
                         doc.description.toLowerCase().includes(searchLower) ||
                         (doc.studentName && doc.studentName.toLowerCase().includes(searchLower)) ||
                         (doc.studentId && doc.studentId.toLowerCase().includes(searchLower));
    
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  console.log(`🔍 Filtered documents: ${filteredDocuments.length} out of ${documents.length} total`);

  const handleViewDocument = (document: any) => {
    setSelectedDocument(document);
    setIsViewerOpen(true);
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await deleteDocument(documentId);
      await enhancedDocumentStorage.deleteDocument(documentId);
      loadStorageStats();
      toast.success('Document deleted successfully');
      console.log(`✅ Document deleted from management page: ${documentId}`);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = (document: any) => {
    downloadDocument(document);
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Manual refresh triggered for 500+ document system');
      await refreshDocuments();
      loadStorageStats();
      toast.success('Documents refreshed successfully');
      console.log(`✅ Manual refresh complete: ${documents.length} documents loaded`);
    } catch (error) {
      console.error('Error refreshing documents:', error);
      toast.error('Failed to refresh documents');
    } finally {
      setIsRefreshing(false);
    }
  };

  const categories = ['all', 'general', 'certificate', 'id', 'academic', 'other'];

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/students">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Students
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Document Management (500+ Capacity)</h1>
            <p className="text-muted-foreground">
              Manage documents for all students - unlimited capacity system
            </p>
          </div>
        </div>
        <Button 
          onClick={handleManualRefresh} 
          disabled={isRefreshing}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Storage Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{documents.length}</div>
            <p className="text-xs text-muted-foreground">500+ capacity system</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{storageStats.totalSizeFormatted}</div>
            <p className="text-xs text-muted-foreground">{storageStats.storageUsage} of quota</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Student Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{documents.filter(doc => doc.studentId).length}</div>
            <p className="text-xs text-muted-foreground">Assigned to students</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Filtered Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredDocuments.length}</div>
            <p className="text-xs text-muted-foreground">Currently showing</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <DocumentFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        categories={categories}
      />

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            All Documents ({filteredDocuments.length}) - 500+ Capacity System
          </CardTitle>
          <CardDescription>
            Documents from all students and general uploads - unlimited capacity
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No documents found</h3>
              <p className="text-muted-foreground mb-4">
                {documents.length === 0 
                  ? 'No documents have been uploaded yet. Upload documents using the Students section to get started.'
                  : searchTerm || selectedCategory !== 'all' 
                    ? 'Try adjusting your search or filter criteria'
                    : 'Upload documents using the Students section to get started'
                }
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Total documents in system: {documents.length}
              </p>
              <Button 
                onClick={handleManualRefresh} 
                variant="outline" 
                className="mt-4"
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh Documents
              </Button>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <DocumentTable
                documents={filteredDocuments}
                onView={handleViewDocument}
                onDelete={handleDeleteDocument}
                onDownload={handleDownloadDocument}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Viewer Modal */}
      {isViewerOpen && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedDocument(null);
          }}
          onDownload={() => handleDownloadDocument(selectedDocument)}
        />
      )}
    </div>
  );
};

export default DocumentManagementPage;
